import type { Editor } from "@tiptap/core";
import type { DirectiveBuilderState } from "../logic/printTable";

export type DirectiveBuilderStep = "directive" | "variable" | "choice";

export type DirectiveFloatingActiveSurface = "list" | "choice";

export type DirectiveSessionKind = "idle" | "floating" | "chip";

export type DirectiveFloatingSessionSnapshot = {
  builder: DirectiveBuilderState;
  directiveTokenFrom: number | null;
  activeSurface: DirectiveFloatingActiveSurface;

  floatingChoiceAtPos: number | null;

  activeFloatingChoiceElement: HTMLElement | null;
};

const getFloatingActiveSurface = (
  builder: DirectiveBuilderState,
): DirectiveFloatingActiveSurface =>
  builder.step === "choice" ? "choice" : "list";

const readFloatingChoiceAtPos = (editor: Editor): number | null =>
  editor.storage.directiveSuggestion?.floatingBuilderChoiceAtPos ?? null;

const deriveFloatingBuilderChoiceAtPos = (
  builder: DirectiveBuilderState,
  directiveTokenFrom: number | null,
): number | null => {
  const pos = directiveTokenFrom;
  if (builder.step === "choice" && typeof pos === "number" && pos >= 0) {
    return pos;
  }
  return null;
};

export const buildFloatingSessionSnapshot = (
  builder: DirectiveBuilderState,
  directiveTokenFrom: number | null,
  activeFloatingChoiceElement: HTMLElement | null = null,
): DirectiveFloatingSessionSnapshot => ({
  builder,
  directiveTokenFrom,
  activeSurface: getFloatingActiveSurface(builder),
  floatingChoiceAtPos: deriveFloatingBuilderChoiceAtPos(
    builder,
    directiveTokenFrom,
  ),
  activeFloatingChoiceElement,
});

export const isFloatingChoiceAtTokenPos = (
  editor: Editor,
  tokenPos: number | undefined,
): boolean => {
  const at = readFloatingChoiceAtPos(editor);
  return typeof tokenPos === "number" && at === tokenPos;
};

export const resolveDirectiveSessionKind = (
  editor: Editor,
  chipPopoverOpen: boolean,
): { kind: DirectiveSessionKind; floatingChoiceAtPos: number | null } => {
  const floatingChoiceAtPos = readFloatingChoiceAtPos(editor);
  if (floatingChoiceAtPos != null) {
    return { kind: "floating", floatingChoiceAtPos };
  }
  if (chipPopoverOpen) {
    return { kind: "chip", floatingChoiceAtPos: null };
  }
  return { kind: "idle", floatingChoiceAtPos: null };
};

export const DIRECTIVE_BUILDER_CHOICE_FIELD_ID = "directive-builder-choice";

export const DIRECTIVE_FLOATING_CHOICE_FIELD_ATTR =
  "data-directive-floating-choice";

export const DIRECTIVE_CHIP_NEXT_CHOICE_TARGET_ATTR =
  "data-directive-chip-next-choice";

export const queryDirectiveBuilderChoiceInRoot = (
  root?: HTMLElement | Document | null,
): HTMLElement | null => {
  const scope = root ?? document.body;
  const byId = scope.querySelector(`#${DIRECTIVE_BUILDER_CHOICE_FIELD_ID}`);
  if (byId instanceof HTMLElement) return byId;
  const byAttr = scope.querySelector(
    `[${DIRECTIVE_FLOATING_CHOICE_FIELD_ATTR}]`,
  );
  return byAttr instanceof HTMLElement ? byAttr : null;
};

export const resolveFloatingChoiceFieldElement = (
  panel: HTMLElement | null,
  explicitEl: HTMLElement | null,
  choiceFieldId: string,
): HTMLElement | null => {
  if (explicitEl != null) return explicitEl;
  if (panel != null) {
    const idScoped = panel.querySelector(`#${choiceFieldId}`);
    if (idScoped instanceof HTMLElement) return idScoped;
    if (choiceFieldId === DIRECTIVE_BUILDER_CHOICE_FIELD_ID) {
      const byAttr = panel.querySelector(
        `[${DIRECTIVE_FLOATING_CHOICE_FIELD_ATTR}]`,
      );
      if (byAttr instanceof HTMLElement) return byAttr;
    }
  }
  const doc = panel?.ownerDocument ?? document;
  return doc.getElementById(choiceFieldId);
};

export const DIRECTIVE_CHIP_CHOICE_FIELD_ATTR =
  "data-directive-chip-choice-field";

export const CHIP_NEXT_CHOICE_SURFACE = "chip-next-choice";
